<template>
  <div id="sales-metrics" class="align-start fill-height">
    <i-overlay v-if="loading" text="Loading your reports data..." />

    <div v-else :class="$route.meta.class">
      <v-dialog
        v-model="p1RangeDialog"
        width="300px"
        persistent
        @click:outside="resetP1Range"
        @keydown.esc="resetP1Range"
      >
        <v-date-picker range v-model="p1Range" :max="currentDate">
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="resetP1Range()"> Cancel </v-btn>
          <v-btn
            text
            color="primary"
            :disabled="p1Range.length !== 2"
            @click="confirmP1Range()"
          >
            Apply
          </v-btn>
          <v-spacer></v-spacer>
        </v-date-picker>
      </v-dialog>

      <v-dialog
        v-model="p2RangeDialog"
        width="300px"
        persistent
        @click:outside="resetP2Range"
        @keydown.esc="resetP2Range"
      >
        <v-date-picker range v-model="p2Range" :max="currentDate">
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="resetP2Range()"> Cancel </v-btn>
          <v-btn
            text
            color="primary"
            :disabled="p2Range.length !== 2"
            @click="confirmP2Range()"
          >
            Apply
          </v-btn>
          <v-spacer></v-spacer>
        </v-date-picker>
      </v-dialog>

      <div class="p-metrics-card">
        <div>
          <v-row align="center">
            <v-col cols="12" sm="4">
              <h2 class="ma-0">{{ $route.meta.title }}</h2>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" sm="4">
              <i-instructions
                instructionKey="reports/sales-metrics"
              ></i-instructions>
            </v-col>
          </v-row>
        </div>

        <div class="my-4">
          <v-card class="px-4 pt-4 pb-2">
            <p class="g-bold g-heading-xs mb-2">Periods Comparison</p>
            <v-row align="start">
              <v-col cols="12" sm="4">
                <v-text-field
                  append-icon="mdi-calendar"
                  background-color="#e5f3ff"
                  class="mb-0 mb-sm-2 p-date-range-field"
                  dense
                  hide-details
                  label="Period 1 (P1)"
                  outlined
                  readonly
                  :value="`${displayP1Range[0]} ~ ${displayP1Range[1]}`"
                  @click="p1RangeDialog = true"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  append-icon="mdi-calendar"
                  background-color="#e5f3ff"
                  class="mb-0 mb-sm-2 p-date-range-field"
                  dense
                  hide-details
                  label="Period 2 (P2)"
                  outlined
                  readonly
                  :value="`${displayP2Range[0]} ~ ${displayP2Range[1]}`"
                  @click="p2RangeDialog = true"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="4">
                <v-btn
                  large
                  rounded
                  class="g-full-width i-btn-block-secondary mt-sm-2"
                  @click="getDashboard"
                  ><strong>Compare</strong></v-btn
                >
              </v-col>
            </v-row>
          </v-card>

          <template v-if="errorCard">
            <v-card class="pa-4 pa-sm-6 pa-md-8 mt-4 mt-md-8">
              <div class="text-center">
                <span
                  >Purchase Metrics could not be generated. Update Periods and
                  compare again.</span
                >
              </div>
            </v-card>
          </template>

          <template v-else>
            <template v-if="$vuetifyDisplay.smAndDown">
              <v-col
                v-for="item in dashboardItems"
                :key="item.titleP1"
                class="mt-6 pa-0"
              >
                <v-card class="rounded-2 px-4 pt-2 mb-6">
                  <v-row no-gutters align="center">
                    <v-col cols="8" class="py-1">
                      <span
                        ><strong>{{ item.titleP1 }}</strong></span
                      >
                    </v-col>
                    <v-col cols="4" class="pl-4">
                      <span>
                        <template v-if="item.d === 'currency'">
                          {{ formatCurrencyMetric(item.p1) }}
                        </template>
                        <template v-else>{{ item.p1 }}</template>
                        <template v-if="item.p === '%' && item.p1 != '-'"
                          >%</template
                        >
                      </span>
                    </v-col>
                    <v-col cols="8" class="py-1">
                      <span
                        ><strong>{{ item.titleP2 }}</strong></span
                      >
                    </v-col>
                    <v-col cols="4" class="pl-4">
                      <span>
                        <template v-if="item.d === 'currency'">
                          {{ formatCurrencyMetric(item.p2) }}
                        </template>
                        <template v-else>{{ item.p2 }}</template>
                        <template v-if="item.p === '%' && item.p2 != '-'"
                          >%</template
                        >
                      </span>
                    </v-col>
                  </v-row>

                  <v-divider class="mt-3"></v-divider>

                  <v-row>
                    <v-col cols="8">
                      <span
                        ><strong>{{ item.titleChange }}</strong></span
                      >
                    </v-col>
                    <v-col cols="4">
                      <p
                        v-if="item.change !== 'N/A'"
                        class="g-bold g-border-radius-s g-heading-s text-center pa-0 px-2"
                        :class="{
                          'p-change-green': item.change > 0,
                          'p-change-red': item.change < 0
                        }"
                      >
                        {{ item.change }} %
                      </p>
                      <p
                        v-else
                        class="g-bold g-border-radius-s g-heading-s text-center pa-0 px-2 p-change-blue"
                      >
                        {{ item.change }}
                      </p>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </template>

            <template v-else>
              <v-row v-for="item in dashboardItems" :key="item.titleP1">
                <v-col cols="6" sm="4">
                  <v-card class="px-4 py-3">
                    <p class="p-card-title mb-2">{{ item.titleP1 }}</p>
                    <p class="g-bold g-heading-m text-center pa-0">
                      <template v-if="item.d === 'currency'">
                        {{ formatCurrencyMetric(item.p1) }}
                      </template>
                      <template v-else>{{ item.p1 }}</template>
                      <template v-if="item.p === '%' && item.p1 != '-'"
                        >%</template
                      >
                    </p>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4">
                  <v-card class="px-4 py-3">
                    <p class="p-card-title mb-2">{{ item.titleP2 }}</p>
                    <p class="g-bold g-heading-m text-center pa-0">
                      <template v-if="item.d === 'currency'">
                        {{ formatCurrencyMetric(item.p2) }}
                      </template>
                      <template v-else>{{ item.p2 }}</template>
                      <template v-if="item.p === '%' && item.p2 != '-'"
                        >%</template
                      >
                    </p>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-card v-if="item.change != 'N/A'" class="px-4 py-3">
                    <p class="p-card-title mb-2">{{ item.titleChange }}</p>
                    <p
                      class="g-bold g-border-radius-s g-heading-m text-center pa-0"
                      :class="{
                        'p-change-green': item.change > 0,
                        'p-change-red': item.change < 0
                      }"
                    >
                      {{ item.change
                      }}<template v-if="item.change != '-'"> %</template>
                    </p>
                  </v-card>
                </v-col>
              </v-row>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import Bugsnag from '@bugsnag/js'
import { mapActions, mapGetters, mapState } from 'vuex'
import { fetchCompareReports } from '@/services/reports'
import iOverlay from '@/components/Overlay'
import iInstructions from '@/components/Instructions'

export default {
  components: {
    iOverlay,
    iInstructions
  },

  data: () => ({
    currentDate: moment().format(),
    leftMenuItems: [
      {
        icon: 'mdi-view-dashboard-outline',
        text: 'Sales Metrics',
        link: 'metrics'
      }
      // { icon: 'mdi-chart-box-outline', text: 'Charts', link: 'charts' },
      // { icon: 'mdi-file-chart', text: 'Download', link: 'download' },
    ],
    errorCard: false,
    loading: true,
    displayP1Range: [],
    displayP2Range: [],
    p1Range: [],
    p1RangeBefore: [],
    p1RangeDialog: false,
    p2Range: [],
    p2RangeBefore: [],
    p2RangeDialog: false,
    selectedSection: 'metrics'
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapGetters('tenant', [
      'tenantCurrency',
      'tenantLocale',
      'usesAustralianTaxLabels'
    ]),

    dashboardItems() {
      return [
        {
          key: 'sales',
          d: 'currency',
          titleP1: this.usesAustralianTaxLabels
            ? 'TOTAL SALES (INC TAX) P1'
            : 'TOTAL SALES P1',
          titleP2: this.usesAustralianTaxLabels
            ? 'TOTAL SALES (INC TAX) P2'
            : 'TOTAL SALES P2',
          titleChange: '% CHANGE'
        },
        {
          key: 'orders',
          titleP1: 'NO. OF ORDERS P1',
          titleP2: 'NO. OF ORDERS P2',
          titleChange: '% CHANGE'
        },
        {
          key: 'avgSales',
          d: 'currency',
          titleP1: this.usesAustralianTaxLabels
            ? 'AVG. SALES / ORDER (INC TAX) P1'
            : 'AVG. SALES / ORDER P1',
          titleP2: this.usesAustralianTaxLabels
            ? 'AVG. SALES / ORDER (INC TAX) P2'
            : 'AVG. SALES / ORDER P2',
          titleChange: '% CHANGE'
        },
        {
          key: 'cartons',
          titleP1: 'TOTAL CARTONS P1',
          titleP2: 'TOTAL CARTONS P2',
          titleChange: '% CHANGE'
        },
        {
          key: 'avgCartons',
          titleP1: 'AVG. CARTONS / ORDER P1',
          titleP2: 'AVG. CARTONS / ORDER P2',
          titleChange: '% CHANGE'
        },
        {
          key: 'nonCartons',
          p: '%',
          titleP1: 'NON SUPPLIED CARTON % P1',
          titleP2: 'NON SUPPLIED CARTON % P2',
          titleChange: '% CHANGE'
        }
      ]
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    // **
    // Helper functions
    // **

    formatDateForApi(date) {
      return moment(date).format('YYYY-MM-DD')
    },

    formatDateForDisplay(date) {
      return moment(date).format('DD/MM/YYYY')
    },

    goTo(url) {
      // Finds HTML element so that the scroll built-in function can be used
      const element = Object.entries(this.$refs).find(
        (entry) => entry[0] === url.toString()
      )?.[1][0]?.$el

      if (element) element.scrollIntoView({ behavior: 'smooth' })
    },

    sortDates() {
      if (this.p1Range[1] > this.p2Range[1]) {
        ;[this.p1Range, this.p2Range] = [this.p2Range, this.p1Range]
      }
    },

    formatCurrencyMetric(value) {
      const numericValue = Number(value)
      if (!Number.isFinite(numericValue)) return value
      return numericValue.toLocaleString(this.tenantLocale, {
        style: 'currency',
        currency: this.tenantCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    // **
    // Functions related to dates management
    // **

    generateP1Range() {
      const secondDate = moment().subtract(1, 'days').subtract(1, 'months')
      const firstDate = moment(secondDate).subtract(1, 'months')
      this.p1Range[1] = this.p1RangeBefore[1] =
        this.formatDateForApi(secondDate)
      this.p1Range[0] = this.p1RangeBefore[0] = this.formatDateForApi(firstDate)
    },

    generateP2Range() {
      const secondDate = moment()
      const firstDate = moment(secondDate).subtract(1, 'months')
      this.p2Range[1] = this.p2RangeBefore[1] =
        this.formatDateForApi(secondDate)
      this.p2Range[0] = this.p2RangeBefore[0] = this.formatDateForApi(firstDate)
    },

    generateDisplayDates() {
      this.displayP1Range[1] = this.formatDateForDisplay(this.p1Range[1])
      this.displayP1Range[0] = this.formatDateForDisplay(this.p1Range[0])
      this.displayP2Range[1] = this.formatDateForDisplay(this.p2Range[1])
      this.displayP2Range[0] = this.formatDateForDisplay(this.p2Range[0])
    },

    confirmP1Range() {
      this.p1Range.sort((d1, d2) => (d1 > d2 ? 1 : -1))
      this.sortDates()
      this.generateDisplayDates()
      this.p1RangeBefore = [...this.p1Range]
      this.p1RangeDialog = false
    },

    resetP1Range() {
      this.p1Range = this.p1RangeBefore
      this.p1RangeDialog = false
    },

    confirmP2Range() {
      this.p2Range.sort((d1, d2) => (d1 > d2 ? 1 : -1))
      this.sortDates()
      this.generateDisplayDates()
      this.p2RangeBefore = [...this.p2Range]
      this.p2RangeDialog = false
    },

    resetP2Range() {
      this.p2Range = this.p2RangeBefore
      this.p2RangeDialog = false
    },

    // **
    // Functions related dashboard
    // **

    getDashboard() {
      this.loading = true
      this.errorCard = false

      const dateToCurrent = this.p2Range[1]
      const dateFromCurrent = this.p2Range[0]
      const dateToPrevious = this.p1Range[1]
      const dateFromPrevious = this.p1Range[0]

      fetchCompareReports(
        this.selectedSupplierId,
        dateFromCurrent,
        dateToCurrent,
        dateFromPrevious,
        dateToPrevious
      )
        .then((reports) => {
          this.populateDashboard(reports)
        })
        .catch((err) => {
          this.errorCard = true
          Bugsnag.notify(err)
          this.addNotification({
            color: 'error',
            text: 'Purchase Metrics could not be updated...'
          })
        })
        .finally(() => {
          this.loading = false
        })
    },

    populateDashboard(data) {
      Object.assign(
        this.dashboardItems.find((item) => item.key === 'sales'),
        {
          p1: data.totalAmountIncGstPrevious || '-',
          p2: data.totalAmountIncGstCurrent || '-',
          change: data.totalAmountChangedPercentage || '-'
        }
      )

      Object.assign(
        this.dashboardItems.find((item) => item.key === 'orders'),
        {
          p1: data.totalNumberOfOrdersPrevious || '-',
          p2: data.totalNumberOfOrdersCurrent || '-',
          change: data.totalNumberOfOrdersChangedPercentage || '-'
        }
      )

      Object.assign(
        this.dashboardItems.find((item) => item.key === 'avgSales'),
        {
          p1: data.averageTotalOrderValueIncGstPrevious || '-',
          p2: data.averageTotalOrderValueIncGstCurrent || '-',
          change: data.averageTotalOrderValueIncGstChangedPercentage || '-'
        }
      )

      Object.assign(
        this.dashboardItems.find((item) => item.key === 'cartons'),
        {
          p1: data.totalOrderedQuantityPrevious || '-',
          p2: data.totalOrderedQuantityCurrent || '-',
          change: data.totalOrderedQuantityChangedPercentage || '-'
        }
      )

      Object.assign(
        this.dashboardItems.find((item) => item.key === 'avgCartons'),
        {
          p1: data.averageTotalOrderedQuantityPrevious || '-',
          p2: data.averageTotalOrderedQuantityCurrent || '-',
          change: data.averageTotalOrderedQuantityChangedPercentage || '-'
        }
      )

      Object.assign(
        this.dashboardItems.find((item) => item.key === 'nonCartons'),
        {
          p1: data.totalMissedQuantityPercentagePrevious || '-',
          p2: data.totalMissedQuantityPercentageCurrent || '-',
          change: 'N/A'
        }
      )
    }
  },

  mounted() {
    this.generateP1Range()
    this.generateP2Range()
    this.generateDisplayDates()
    this.getDashboard()
  }
}
</script>

<style lang="scss">
.p-date-range-field {
  * {
    cursor: pointer !important;
  }
}
</style>

<style lang="scss" scoped>
#sales-metrics {
  scroll-behavior: smooth;
  .p-metrics-card {
    max-width: 960px;
    margin: auto;
  }
  .p-change-blue {
    background: $blue-color-bg;
    color: $blue-color;
    padding-right: 25px;
  }
  .p-change-red {
    background: $red-color-bg;
    color: $red-color;
    padding-right: 25px;
  }
  .p-change-green {
    background: $green-color-bg;
    color: $green-color;
  }
  .p-date-range-field {
    background-color: $primary-color-light;
    .v-input__control {
      div {
        cursor: pointer !important;
      }
      .v-input__slot {
        fieldset {
          border: 2px solid $primary-color !important;
        }
      }
    }
  }
}

@media only screen and (min-width: 960px) {
  #sales-metrics {
    .p-card-title {
      min-height: 40px;
    }
  }
}

@media only screen and (min-width: 1340px) {
  #sales-metrics {
    .p-card-title {
      min-height: unset;
    }
  }
}
</style>
